import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, RadialGradient, Rect, Stop } from 'react-native-svg';

type Props = {
  width: number;
  height: number;
};

/**
 * Daytime wallpaper used by the `auto` theme. Rendered as SVG (not a PNG asset)
 * so the build does not need to ship per-density bitmaps and so the gradient
 * stays crisp at any DPI. The rays are drawn as long, semi-transparent rects
 * rotated about the sun's center — cheap to rasterise compared with separate
 * Path nodes, and they blend nicely under the screen scrim.
 */
export function SunWallpaper({ width, height }: Props) {
  // Sun positioned in the upper-third so it doesn't visually fight with the
  // header title; layout values are recomputed only on resize.
  const layout = useMemo(() => {
    const cx = width * 0.78;
    const cy = height * 0.18;
    const sunR = Math.min(width, height) * 0.13;
    const rayRays = 14;
    return { cx, cy, sunR, rayRays };
  }, [width, height]);

  return (
    <View style={[StyleSheet.absoluteFill, { width, height }]} pointerEvents="none">
      <Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <Defs>
          <LinearGradient id="sunSky" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0%" stopColor="#fde68a" stopOpacity={1} />
            <Stop offset="55%" stopColor="#fcd34d" stopOpacity={1} />
            <Stop offset="100%" stopColor="#fb923c" stopOpacity={1} />
          </LinearGradient>
          <RadialGradient
            id="sunHalo"
            cx={String(layout.cx)}
            cy={String(layout.cy)}
            r={String(layout.sunR * 3.2)}
            fx={String(layout.cx)}
            fy={String(layout.cy)}
            gradientUnits="userSpaceOnUse">
            <Stop offset="0%" stopColor="#fffbeb" stopOpacity={0.9} />
            <Stop offset="55%" stopColor="#fde68a" stopOpacity={0.45} />
            <Stop offset="100%" stopColor="#fde68a" stopOpacity={0} />
          </RadialGradient>
          <RadialGradient
            id="sunBody"
            cx={String(layout.cx)}
            cy={String(layout.cy)}
            r={String(layout.sunR)}
            fx={String(layout.cx)}
            fy={String(layout.cy)}
            gradientUnits="userSpaceOnUse">
            <Stop offset="0%" stopColor="#fffbeb" stopOpacity={1} />
            <Stop offset="60%" stopColor="#fde047" stopOpacity={1} />
            <Stop offset="100%" stopColor="#f59e0b" stopOpacity={1} />
          </RadialGradient>
        </Defs>

        {/* Sky gradient */}
        <Rect x={0} y={0} width={width} height={height} fill="url(#sunSky)" />

        {/* Soft warm halo behind the sun */}
        <Circle cx={layout.cx} cy={layout.cy} r={layout.sunR * 3.2} fill="url(#sunHalo)" />

        {/* Sun rays — drawn as faint thin rectangles emanating from the disc */}
        {Array.from({ length: layout.rayRays }).map((_, i) => {
          const angle = (i * 360) / layout.rayRays;
          return (
            <Rect
              key={`ray-${i}`}
              x={layout.cx - 1.5}
              y={layout.cy - layout.sunR * 3.4}
              width={3}
              height={layout.sunR * 2.6}
              fill="#fef3c7"
              opacity={0.55}
              transform={`rotate(${angle} ${layout.cx} ${layout.cy})`}
            />
          );
        })}

        {/* Sun disc */}
        <Circle cx={layout.cx} cy={layout.cy} r={layout.sunR} fill="url(#sunBody)" />
      </Svg>
    </View>
  );
}
